// НАВИГАЦИЯ ШАГА 3 — вид из адреса и ряд футера для каждого положения. B5-2.
//
// ЗДЕСЬ ДВА ВОПРОСА, И ОНИ СОСЕДИ ПО УСТРОЙСТВУ: «где человек стоит» (вид + карточка + панель,
// всё это читается из адреса) и «что написано на двух кнопках внизу» (таблица §5 спеки). Второе
// — чистая функция первого, поэтому они живут в одном модуле и проверяются без монтирования.
//
// ⛔ ПОЧЕМУ ЭТО НЕ `footerNav.js`. Та таблица отвечает на вопрос «какая ПАРА стоит внизу» и по
// устройству не выражает шаг 3: борду нужны ПЯТЬ надписей CTA и ЧЕТЫРЕ разных back, причём выбор
// зависит от панели и от гейта, а не от шага. Тикет B5-2 прямо запрещает трогать `CTA_LABEL` и
// называет способ — канал `publishGate`. То есть у шага 3 свой ряд надписей, а печатает его
// по-прежнему оболочка, и второй правды о хроме не заводится.
//
// ⭐ КАНАЛ ПРИШЛОСЬ РАСШИРИТЬ НА `back`, И ЭТО НАЗВАНО ДО КОДА (приёмка B5-2 разрешает). До B5-2
// `publishGate` нёс только CTA: у шага 2 надпись возврата одна на весь визард (`← back`), и
// вопроса не возникало. У шага 3 их четыре, и все четыре ведут ВНУТРЬ шага — панель к панели,
// карточка к карточке, карточка I к интро. Оболочка про внутренности шага не знает и знать не
// должна, поэтому расширение ровно то же самое, что уже ратифицировано для CTA: экран ОБЪЯВЛЯЕТ
// надпись и обработчик, оболочка печатает и спрашивает. Альтернатива — второй уровень в
// `CTA_LABEL` — запрещена тикетом и завела бы правило шага в чужую полосу.
//
// ⚠️ ГЕЙТ ЗДЕСЬ — ПАРАМЕТР, А НЕ ВЫЧИСЛЕНИЕ. Арифметика уже написана (`deckGate.js`, B5-1), и
// звать её отсюда значило бы прибить таблицу надписей к форме конверта. Кто считает гейт —
// вопрос экрана; какая надпись при закрытом гейте — вопрос этой таблицы.

import { formatAddress } from '../address.js';
import { LAYERS } from '../../contracts.js';
import { GROUP_IDS } from '../toWizardInput.js';
import { FLOOR, nSliders } from './deckGate.js';
import { hardCta } from './deckDone.js';

/** Три вида шага 3 (спека §9: `intro → deck → done`). */
export const VIEW = { INTRO: 'intro', DECK: 'deck', DONE: 'done' };

/** Панели карточки в порядке борда. Буква подэкрана адреса `#s3/2/b` — индекс в этом массиве. */
export const PANE_SUBS = ['a', 'b', 'c'];

/**
 * Где человек стоит, из разобранного адреса и числа этапов.
 *
 * ⭐ У ВИДА `done` СВОЙ АДРЕС, И ЭТО РЕШЕНИЕ CC, А НЕ НАХОДКА В СПЕКЕ. Грамматика `#s3/N/p`
 * (`address.js`, канон хрома §4) знает только карточку и подэкран — вида `done` в ней нет вовсе.
 * Но приёмка B5-2 требует, чтобы положение ПЕРЕЖИВАЛО ПЕРЕЗАГРУЗКУ, а финал — это место, где
 * человек стоит подолгу (стена, кнопки прыжка на недобранные карточки). Поэтому финал занимает
 * номер СРАЗУ ЗА последней карточкой: `#s3/{N+1}`. Ни одной новой буквы в грамматику это не
 * добавляет, `parseAddress` разбирает его уже сегодня, и карточки с таким номером не существует
 * по построению. Если web-Claude решит иначе — меняется эта функция и больше ничего.
 *
 * @param {{card: number|null, sub: string|null}|null} parsed разобранный адрес
 * @param {number} stageCount число этапов в конверте
 */
export function deckWhere(parsed, stageCount) {
  const card = parsed && Number.isInteger(parsed.card) ? parsed.card : null;
  if (card === null) return { view: VIEW.INTRO, card: null, pane: null };
  if (card > stageCount) return { view: VIEW.DONE, card: null, pane: null };

  const pane = PANE_SUBS.indexOf(parsed.sub);
  // Подэкрана нет или он чужой — панель A. Адрес `#s3/2` законен грамматикой и означает
  // «карточка два», а с какой панели её открыть — вопрос не адреса: открывается первая.
  return { view: VIEW.DECK, card, pane: pane < 0 ? 0 : pane };
}

/** Адрес карточки и панели — через `formatAddress`, чтобы форма собиралась в одном месте. */
export const cardAddress = (card, pane = 0) => formatAddress({ step: 3, card, sub: PANE_SUBS[pane] });

/** Адрес финала — `#s3/{N+1}`, см. `deckWhere`. */
export const doneAddress = (stageCount) => formatAddress({ step: 3, card: stageCount + 1 });

/** Адрес интро — голый `#s3`. */
export const introAddress = () => formatAddress({ step: 3 });

/** Адрес шага 4 — цель открытой CTA финала. Номер один, второй копии нет. */
export const NEXT_STEP_ADDRESS = formatAddress({ step: 4 });

/**
 * ПОПРАВКА АДРЕСА (B5-6, пункт 4 скоупа) — два адреса, которые ГРАММАТИКА разбирает, а СОСТОЯНИЕ
 * не подтверждает.
 *
 * ⭐ ЭТО ТРЕТИЙ УРОВЕНЬ, И ОН БЫЛ ПРОПУЩЕН ДВАЖДЫ. Первый — грамматика (`parseAddress`: строка
 * годная). Второй — картинка (`paneLocked`, `gateOK`: точка заперта, CTA закрыта). Третьего не
 * было вовсе, и потому запертая точка B честно не пускала, а прямой `#s3/1/b` открывал ту же
 * панель в обход — то есть замок стоял на кнопке, а не на двери. Ровно то же с финалом: точка и
 * CTA его берегли, адрес `#s3/{N+1}` — нет.
 *
 * ⛔ ПОПРАВКА — ПОДМЕНА ЗАПИСИ, А НЕ ПЕРЕХОД, и вызывающему это назначено здесь, а не оставлено
 * на его усмотрение: при `pushState` браузерный «назад» вернул бы человека на тот самый адрес,
 * который экран только что поправил, поправка сработала бы снова, и кнопка «назад» перестала бы
 * работать вовсе.
 *
 * ⚠️ У ПУСТОГО КОНВЕРТА ЦЕЛЬ ДРУГАЯ, И ЭТО НЕ ПЕДАНТИЗМ. При нуле этапов `cardAddress(1)` — это
 * `#s3/1`, то есть `card > stageCount`, то есть СНОВА финал: поправка увела бы сама в себя, и
 * получился бы вечный цикл подмен. Ноль этапов ведёт на интро — единственный вид, у которого
 * деки под ногами нет по построению.
 *
 * @param {{view: string, card: number|null, pane: number|null}} where результат `deckWhere`
 * @param {{stageCount: number, gateOpen?: boolean, doneOpen?: boolean}} ctx
 * @returns {string|null} адрес поправки, `null` = адрес подтверждён состоянием
 */
export function deckRedirect(where, ctx) {
  const { stageCount, gateOpen = true, doneOpen = true } = ctx;

  if (where.view === VIEW.DONE) {
    if (doneOpen) return null;
    return stageCount > 0 ? cardAddress(1, 0) : introAddress();
  }
  // Панель B или C при закрытом гейте — на панель A ТОЙ ЖЕ карточки: человек назвал карточку
  // верно, и уводить его с неё не за что. Возражает экран только про подэкран.
  if (where.view === VIEW.DECK && where.pane > 0 && !gateOpen) return cardAddress(where.card, 0);
  return null;
}

/**
 * СЧЁТ И ПОЛНОТА ПАНЕЛИ (борд `paneCount` `:496`, `paneDone` `:497`) — приехало с B5-3 вместе с
 * точками панелей, которым этот счёт и нужен.
 *
 * ⚠️ СЧИТАЕТСЯ ПО КОНВЕРТУ, А НЕ ПО ЭКРАНУ, и это важно именно сейчас. Панелей B и C на экране нет
 * (B5-4), но их РЯДЫ в конверте есть уже сегодня, и восстановленная сессия может принести в них
 * работу. Заглушка `0/4` соврала бы ровно такому человеку; здесь же число приходит из того же
 * места, откуда его возьмёт B5-4, и менять эту функцию тот тикет не будет.
 *
 * ⛔ ПОЛНОТА — НЕ ГЕЙТ. `done` панели A это ВСЕ СЕМЬ слоёв (борд: `fullS`), а гейт открывается на
 * двух (`FLOOR`). Свести их в одно значило бы либо запереть человека до семи, либо красить точку
 * зелёным на двух — оба ответа неверны, и оба уже обсуждались (`s3-gate-plan.md`, «в 3,5 раза выше
 * пола»). Поэтому `gateOK` здесь не зовётся вовсе.
 *
 * @param {number} pane индекс панели (0=A, 1=B, 2=C)
 * @param {{sliders?: Array, buttons?: Array, text?: Array}} rows ряды конверта
 * @param {number} card номер карточки, с единицы
 * @returns {{count: string, done: boolean}}
 */
export function paneStat(pane, rows, card) {
  const i = card - 1;
  const at = (name) => (Array.isArray(rows && rows[name]) ? rows[name][i] : undefined);

  if (pane === 0) {
    const n = nSliders(at('sliders'));
    return { count: `${n}/${LAYERS.length}`, done: n === LAYERS.length };
  }
  if (pane === 1) {
    const row = at('buttons');
    const n = row ? GROUP_IDS.filter((id) => Array.isArray(row[id]) && row[id].length).length : 0;
    return { count: `${n}/${GROUP_IDS.length}`, done: n === GROUP_IDS.length };
  }
  const row = at('text');
  const keys = ['T1', 'T2', 'T3'];
  const n = row ? keys.filter((k) => typeof row[k] === 'string' && row[k].trim()).length : 0;
  return { count: `${n}/${keys.length}`, done: n === keys.length };
}

/**
 * МИКРО-СТРОКА ПОДВАЛА КАРТОЧКИ (узел `#micro` борда `:512`, содержимое — `paintFoot` `:577`). B5-8.
 *
 * ⭐ СТРОК ТРИ, А НЕ ОДНА, И ЗАГОЛОВОК ТИКЕТА НАЗЫВАЛ ТОЛЬКО ПЕРВУЮ. Узел у борда один, а что в
 * нём написано — зависит от панели: A — `{n}/7 layers on this card` с хвостом при недоборе, B —
 * `{n}/4 choices made`, C — `{n}/3 answered · all three optional`. Все три названы в спеке
 * поимённо («микро A / микро B / микро C», `docs/design/cc-webclaude-s3-fields-20-08.md:281–283`),
 * и в корпусе не было ни одной. Закрыты все три (`s934.d1` п. 2): возьми только первую — панели B
 * и C остались бы без дома и без тикета, то есть потерялись бы молча.
 *
 * ⭐ ЧИСЛО БЕРЁТСЯ У `paneStat`, А НЕ СЧИТАЕТСЯ ЗАНОВО. Та же строка `n/7` уже стоит в точках
 * панелей — борд и там, и здесь зовёт один `paneCount`. Вторая арифметика того же числа разошлась
 * бы с первой молча, и увидели бы мы это не тестом, а глазом на экране.
 *
 * ⚠️ ХВОСТ ПАНЕЛИ A ОТДАЁТСЯ ОТДЕЛЬНЫМИ ПОЛЯМИ, ПОТОМУ ЧТО У НЕГО СВОЙ УЗЕЛ. У борда
 * `two is the floor` одет в `.slot` (пунктир под строкой), а разделитель ` · ` стоит ВНЕ него.
 * Склеенная строка потеряла бы границу узла — ровно так же, как склеенная надпись CTA потеряла
 * бы `.n` вокруг числа.
 *
 * ⛔ ВИДЫ `intro` И `done` ОТДАЮТ `null`, И ЭТО НЕ ЗАБЫТАЯ ВЕТКА. У обоих своя микро-строка, и
 * обе уже стоят в теле (`INTRO_SPARKNOTE` в `deckIntro.js`, `doneFoot` в `deckDone.js`). Второй
 * источник той же строки — дрейф, а не удобство.
 *
 * ⚠️ ГЕЙТ, КАК И В ТАБЛИЦЕ ПОДВАЛА, — ПАРАМЕТР, А НЕ ВЫЧИСЛЕНИЕ: кто считает гейт, решает экран.
 *
 * @param {{view: string, card: number|null, pane: number|null}} where результат `deckWhere`
 * @param {{sliders?: Array, buttons?: Array, text?: Array}} rows ряды конверта
 * @param {{gateOpen?: boolean}} [ctx] состояние гейта этой карточки
 * @returns {{bold: string, trail: string, tailLead: string|null, tailSlot: string|null}|null}
 */
export function deckMicro(where, rows, { gateOpen = true } = {}) {
  if (where.view !== VIEW.DECK) return null;

  const { count } = paneStat(where.pane, rows, where.card);
  if (where.pane === 1) {
    return { bold: count, trail: ' choices made', tailLead: null, tailSlot: null };
  }
  if (where.pane === 2) {
    return { bold: count, trail: ' answered · all three optional', tailLead: null, tailSlot: null };
  }
  return {
    bold: count,
    trail: ' layers on this card',
    tailLead: gateOpen ? null : ' · ',
    tailSlot: gateOpen ? null : 'two is the floor',
  };
}

/**
 * ТАБЛИЦА ПОДВАЛА — §5 спеки («Переключение панелей + копи подвала», `paintFoot()` борда `:569`).
 *
 * Пять надписей CTA и четыре back, дословно. Возвращает надписи и ЦЕЛИ, а не только строки:
 * пара «что написано» и «куда ведёт» разъезжается ровно тогда, когда живёт в двух местах.
 *
 * ⚠️ `move {N} more to continue` СЕГОДНЯ С ЭКРАНА НЕДОСТИЖИМА, И ЭТО НЕ ЗАБЫТАЯ ВЕТКА. Панелей в
 * B5-2 нет — двигать нечего, — поэтому экран зовёт таблицу с `gateOpen: true`. Строка при этом
 * написана и проверена здесь, как `FIELD_MAX` из B5-1 проверен без поля: её достижимость придёт
 * вместе с ползунками (B5-3), а не вместе с новой строкой.
 *
 * @param {{view: string, card: number|null, pane: number|null}} where результат `deckWhere`
 * @param {{stageCount: number, gateOpen?: boolean, touched?: number, firstStageName?: string,
 *          hard?: number[]}} ctx `hard` — индексы карточек под полом, нужен только финалу
 * @returns {{ctaLabel: string|null, ctaTo: string|null, backLabel: string|null, backTo: string|null}}
 */
export function deckFooter(where, ctx) {
  const {
    stageCount, gateOpen = true, touched = 0, firstStageName = '', hard = [],
  } = ctx;

  if (where.view === VIEW.INTRO) {
    return {
      // Строка интро — копи вида, а не подвала деки: дом у неё в `deckIntro.js`, и повторять её
      // здесь нельзя. Вызывающий подставляет имя сам, здесь только цель.
      ctaLabel: null,
      ctaTo: stageCount > 0 ? cardAddress(1, 0) : null,
      backLabel: null,
      backTo: null,
      firstStageName,
    };
  }

  // ⭐ ФИНАЛ ПРИЕХАЛ (B5-6). До него здесь стояла пустая рамка и оболочка печатала свою пару по
  // умолчанию — названная граница двух тикетов, снята намеренно.
  //
  // ⚠️ НАДПИСЬ ЗАКРЫТОЙ CTA СЧИТАЕТ НЕ ЭТА ТАБЛИЦА (`hardCta`, `deckDone.js`), и дом у неё там не
  // по вкусу: она несёт ЧИСЛО недобранных карточек, то есть принадлежит арифметике гейта, а не
  // таблице надписей. Здесь — только выбор ветки и цели, как у остальных пяти.
  //
  // ⛔ ВОЗВРАТ ВЕДЁТ НА ПАНЕЛЬ A ПОСЛЕДНЕЙ КАРТОЧКИ, А НЕ НА ПАНЕЛЬ C, С КОТОРОЙ ПРИШЛИ — байт
  // борда (`#back.onclick`: `S.i=STAGES.length-1;S.pane=0`). Это не «вернуть как было»: финал
  // говорит про карточки целиком, и человек, нажавший `← last card`, идёт перечитывать карточку,
  // а не дописывать третье поле.
  if (where.view === VIEW.DONE) {
    const back = {
      backLabel: '← last card',
      backTo: stageCount > 0 ? cardAddress(stageCount, 0) : introAddress(),
    };
    const shut = hardCta(hard);
    if (shut) {
      return {
        ctaLabel: null,
        ctaCount: shut.count,
        ctaLead: shut.lead,
        ctaTrail: shut.trail,
        ctaTo: null,
        ...back,
      };
    }
    return { ctaLabel: 'see all seven layers', ctaTo: NEXT_STEP_ADDRESS, ...back };
  }

  const { card, pane } = where;
  const last = card === stageCount;

  // BACK — четыре ветки. Панель B и C возвращают на соседнюю слева; панель A уводит с карточки.
  const back = pane > 0
    ? {
      backLabel: pane === 1 ? '← A · the seven layers' : '← B · the four choices',
      backTo: cardAddress(card, pane - 1),
    }
    : (card > 1
      ? { backLabel: '← previous card', backTo: cardAddress(card - 1, 0) }
      : { backLabel: '← back to the list', backTo: introAddress() });

  // CTA — пять веток. Закрытый гейт перебивает панель A; дальше порядок панелей, и последняя
  // карточка на панели C уводит на финал, а не на шестую карточку.
  if (pane === 0 && !gateOpen) {
    return {
      ctaLabel: null,
      ctaCount: Math.max(0, FLOOR - touched),
      ctaLead: 'move ',
      ctaTrail: ' more to continue',
      ctaTo: null,
      ...back,
    };
  }
  if (pane === 0) return { ctaLabel: 'next · the four choices', ctaTo: cardAddress(card, 1), ...back };
  if (pane === 1) return { ctaLabel: 'next · your own words', ctaTo: cardAddress(card, 2), ...back };
  return last
    ? { ctaLabel: 'draw the map', ctaTo: doneAddress(stageCount), ...back }
    : { ctaLabel: 'next card', ctaTo: cardAddress(card + 1, 0), ...back };
}
